// Tenantra Inc. - Tenant Screening product page (ID + rental history available, credit report in development)
import { Check } from 'lucide-react';
import { SubPageLayout, Section, FlowSteps, MechanicRows, FAQList } from '@/components/public/SubPageLayout';

const STEPS = [
  { title: 'Invite the applicant', desc: 'Send a screening link by email or SMS. The applicant completes it on their phone in a few minutes.' },
  { title: 'Identity verified', desc: 'Government ID and liveness check through Didit. You know the person applying is the person on the ID.' },
  { title: 'Rental history pulled', desc: 'Any rent history reported through Tenantra by previous verified landlords: on time, late, missed and balance owing.' },
  { title: 'Credit report', desc: 'An Equifax credit report, with the applicant’s consent, delivered alongside the rental history.', note: 'In development. Not yet available.' },
];

const MECHANICS = [
  { title: 'Identity verification', desc: 'Document authenticity, face match and liveness. The same verification every Tenantra landlord goes through, applied to applicants.' },
  { title: 'Verified rental history', desc: 'Only history reported by identity-verified landlords on verified properties. No reference letters, no screenshots.' },
  { title: 'Credit report (in development)', desc: 'Pulled from Equifax Canada with the applicant’s explicit consent and shown next to their rent history, so you see debt and rent behaviour together.' },
  { title: 'Applicant privacy', desc: 'The applicant sees exactly what you will receive before they consent. Screening data is kept only as long as needed for the application.' },
  { title: 'Verified landlords only', desc: 'Screening is available only to verified landlords with a verified property, so the system cannot be used to collect data on people who never applied.' },
];

const FAQ = [
  { q: 'What can I screen with today?', a: 'Identity verification and verified rental history reported through Tenantra. The Equifax credit report is in progress and will be added to the same screening flow.' },
  { q: 'Does screening affect the applicant’s credit score?', a: 'Identity verification and rental history have no effect on credit. When credit reports are added, the applicant will be told what type of inquiry is used before consenting.' },
  { q: 'What if the applicant has no Tenantra history?', a: 'You still receive the identity verification result. Once they rent from you and consent to reporting, their history starts building.' },
  { q: 'Can I screen without the applicant knowing?', a: 'No. Every screening requires the applicant to verify themselves and consent, and they see what is shared.' },
];

const SUMMARY = [
  ['Identity', 'Verified. Ontario driver’s licence, face match passed.', true],
  ['Rental history', '8 months reported. 8 on time, $0 owing.', true],
  ['Previous landlord', 'Verified. Thompson Properties, Toronto.', true],
  ['Credit report', 'Not yet available.', false],
];

const Summary = () => (
  <div className="bg-white border border-stone-200 rounded-lg" data-testid="screening-summary-preview">
    <div className="px-6 py-5 border-b border-stone-200 flex flex-wrap items-baseline justify-between gap-2">
      <p className="text-base font-semibold text-stone-900">S. Chen, applicant</p>
      <p className="text-sm text-stone-500">3650 Kaneff Cres, Unit 1507 · Jun 14, 2026</p>
    </div>
    <dl className="divide-y divide-stone-100">
      {SUMMARY.map(([k, v, ok]) => (
        <div key={k} className="grid sm:grid-cols-[180px_1fr] gap-1 sm:gap-6 px-6 py-4 text-sm">
          <dt className="text-stone-500">{k}</dt>
          <dd className={`flex items-start gap-2 ${ok ? 'text-stone-900' : 'text-stone-400'}`}>
            {ok && <Check className="w-4 h-4 text-teal-700 mt-0.5 flex-shrink-0" strokeWidth={2.5} />}
            {v}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

export default function ScreeningPage() {
  return (
    <SubPageLayout
      active="screening"
      eyebrow="Tenant Screening"
      title="Know who you’re renting to before you sign."
      intro="Verify the applicant’s identity and see rent history reported by previous verified landlords. A full Equifax credit report, with the applicant’s consent, is being added to the same flow."
      availability={[
        { on: true, text: 'Identity verification and rental history: available now' },
        { on: false, text: 'Equifax credit report: in development' },
      ]}
      primary={{ label: 'Screen an applicant', href: '/register' }}
      secondary={{ label: 'How reporting works', href: '/rent-reporting' }}
      image="https://images.pexels.com/photos/7642004/pexels-photo-7642004.jpeg?auto=compress&cs=tinysrgb&w=1200"
      imageAlt="Landlord handing keys to a new tenant"
    >
      <Section eyebrow="How it works" title="One link to the applicant, one summary back to you" testId="screening-flow">
        <FlowSteps steps={STEPS} />
      </Section>
      <Section tone="sand" eyebrow="What you receive" title="Verified facts, on one page" intro="The summary you get back once the applicant finishes. Every line comes from a verification, not from what the applicant typed in." testId="screening-preview">
        <div className="max-w-3xl">
          <Summary />
          <p className="mt-4 text-sm text-stone-500">Sample applicant.</p>
        </div>
      </Section>
      <Section eyebrow="Details" title="What goes into a Tenantra screening" testId="screening-mechanics">
        <MechanicRows rows={MECHANICS} />
      </Section>
      <Section tone="sand" eyebrow="Questions" title="Common questions" testId="screening-faq">
        <FAQList items={FAQ} />
      </Section>
    </SubPageLayout>
  );
}
